import { useMemo, useState } from "react";

import { formatDashboardDayLabel } from "../../lib/dashboardDayLabel.js";
import {
  ACTIVITY_STEP,
  type ActivityResolution,
  aggregatePresenceActivity,
  possibleTableMinutes,
  PRESENCE_MINUTE,
} from "../../lib/presenceActivity.js";
import type { OpenRawEventsHandler, PresenceData } from "../../types.js";
import { ChartCard } from "../ChartCard.js";

const COLORS = ["#31c48d", "#5aa2ff", "#f9c74f", "#bd9bff", "#526480"];
const WIDTH = 1000;
const HEIGHT = 300;
const LEFT = 45;
const RIGHT = 15;
const TOP = 15;
const BOTTOM = 45;
const PLOT_WIDTH = WIDTH - LEFT - RIGHT;
const PLOT_HEIGHT = HEIGHT - TOP - BOTTOM;

type Metric = "phoneCounts" | "advertiserCounts" | "activity";
type Point = {
  time: number;
  values: number[] | null;
  counts: number[] | null;
  observed: number;
  reported: number;
};

function distanceLabel(bounds: PresenceData["distanceBuckets"][number]) {
  if (bounds.minM === null) return "Unknown distance";
  if (bounds.maxM === null) return `${bounds.minM} m+`;
  return `${bounds.minM}–${bounds.maxM} m`;
}
function timeLabel(time: number) {
  return new Date(time).toISOString().replace("T", " ").slice(0, 16) + " UTC";
}
function pct(value: number, total: number) {
  return total ? `${((value / total) * 100).toFixed(1)}%` : "—";
}

export function PresenceTrendChart({
  data,
  onOpenRawEvents,
}: {
  data: PresenceData;
  onOpenRawEvents: OpenRawEventsHandler;
}) {
  const [metricOverride, setMetric] = useState<Metric | null>(null);
  const [resolution, setResolution] = useState<ActivityResolution | "auto">("auto");
  const [windowMinutes, setWindowMinutes] = useState(0);
  const [focusRange, setFocusRange] = useState<{ from: number; to: number } | null>(null);
  const [inspectedTime, setInspectedTime] = useState<number | null>(null);
  const rangeMs = Date.parse(data.range.to) - Date.parse(data.range.from);
  const metric = metricOverride ?? (rangeMs > 2 * 86400000 ? "activity" : "phoneCounts");
  const activity = metric === "activity";
  const effectiveRange = windowMinutes ? windowMinutes * PRESENCE_MINUTE : rangeMs;
  const automatic =
    effectiveRange > 14 * 86400000 ? "day" : effectiveRange > 2 * 86400000 ? "hour" : "minute";
  const grain = activity ? (resolution === "auto" ? automatic : resolution) : "minute";
  const step = ACTIVITY_STEP[grain];
  const points = useMemo<Point[]>(
    () =>
      activity
        ? aggregatePresenceActivity(data.trend, grain).map((row) => ({ ...row }))
        : data.trend.map((row) => ({
            time: Date.parse(row.bucket),
            values: row[metric],
            counts: row[metric],
            observed: row.successfulTables,
            reported: row.reportingTables,
          })),
    [activity, data.trend, grain, metric],
  );
  const byTime = useMemo(() => new Map(points.map((point) => [point.time, point])), [points]);
  const last = focusRange
    ? Math.floor(focusRange.to / step) * step
    : activity
      ? Math.floor(Date.parse(data.range.to) / step) * step
      : (points.at(-1)?.time ?? Date.parse(data.range.to));
  const first = focusRange
    ? Math.floor(focusRange.from / step) * step
    : activity
      ? Math.floor(Date.parse(data.range.from) / step) * step
      : (points[0]?.time ?? last);
  const start = windowMinutes
    ? Math.max(
        first,
        last - Math.max(0, Math.ceil((windowMinutes * PRESENCE_MINUTE) / step) - 1) * step,
      )
    : first;
  const visible = points.filter((point) => point.time >= start && point.time <= last);
  const xMin = start - step / 2;
  const xMax = last + step / 2;
  const x = (time: number) => LEFT + ((time - xMin) / (xMax - xMin)) * PLOT_WIDTH;
  const maximum = visible.reduce(
    (max, point) =>
      Math.max(
        max,
        (point.values ?? []).reduce((sum, value) => sum + value, 0),
      ),
    1,
  );
  const yMax = activity ? 100 : maximum <= 4 ? maximum : Math.ceil(maximum / 4) * 4;
  const y = (value: number) => TOP + PLOT_HEIGHT - (value / yMax) * PLOT_HEIGHT;
  const runs: Point[][] = [];
  for (const point of visible) {
    if (!point.values || !point.observed) continue;
    const run = runs.at(-1);
    if (run && point.time - run.at(-1)!.time === step) run.push(point);
    else runs.push([point]);
  }
  const cumulative = (point: Point, series: number) =>
    point.values!.slice(0, series + 1).reduce((sum, value) => sum + value, 0);
  const hoverTime =
    inspectedTime !== null && inspectedTime >= start && inspectedTime <= last
      ? Math.floor(inspectedTime / step) * step
      : (visible.at(-1)?.time ?? last);
  const hovered = byTime.get(hoverTime);
  const labels = [
    ...data.distanceBuckets.map(distanceLabel),
    ...(activity ? ["No phone signal"] : []),
  ];
  const possible = possibleTableMinutes(hoverTime, step, data.range, data.summary.tables);
  const tickCount = Math.min(6, Math.max(1, Math.round((last - start) / step) + 1));
  const ticks = [
    ...new Set(
      Array.from(
        { length: tickCount },
        (_, i) =>
          Math.round((start + ((last - start) * i) / Math.max(1, tickCount - 1)) / step) * step,
      ),
    ),
  ];
  const showDay =
    new Date(start).toISOString().slice(0, 10) !== new Date(last).toISOString().slice(0, 10);
  const openInterval = (time: number, event?: Parameters<OpenRawEventsHandler>[1]) => {
    if (!byTime.has(time)) return;
    onOpenRawEvents(
      {
        eventName: "ble_presence_snapshot",
        gameUrl: "all",
        drillDownFrom: new Date(Math.max(time, Date.parse(data.range.from))).toISOString(),
        drillDownTo: new Date(Math.min(time + step - 1, Date.parse(data.range.to))).toISOString(),
      },
      event,
    );
  };
  const pointerTime = (event: React.MouseEvent<SVGSVGElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    const svgX = ((event.clientX - box.left) / box.width) * WIDTH;
    const time = Math.round((xMin + ((svgX - LEFT) / PLOT_WIDTH) * (xMax - xMin)) / step) * step;
    return Math.min(last, Math.max(start, time));
  };

  return (
    <ChartCard
      title={activity ? `Presence activity by ${grain} (UTC)` : "Nearby devices by minute (UTC)"}
      criteria={`device_events.event_name = ble_presence_snapshot; venue/device/date filters, no Game filter, fleet/rejected/public-venue filters apply. For each table and UTC minute take the latest snapshot, ordered by client_timestamp, received_at, id. ${
        activity
          ? `Classify each successful table-minute once by its closest occupied possible-phone distance bucket (phone_evidence + phone_candidate), otherwise unknown distance or no phone signal. Sum those observations by UTC ${grain}, then divide each band by total successful table-minutes in that interval. Never sum phone counts over time, average percentages, carry values forward, or count missing/failed scans as absence. Coverage denominator is possible calendar table-minute slots for tables reporting anywhere in the selected range; this is not expected awake time. Example: 15 near-table observations / 60 successful table-minutes = 25% activity.`
          : "Sum the four distance counts across successful table readings. Possible phones = phone_evidence + phone_candidate; All advertisers = all five categories. Counts retain minute resolution; there is no aggregation over time."
      } Missing or wholly unavailable intervals break the line. Partial coverage is shown below. Click opens all BLE events in that exact interval, clipped to selected bounds; no exact distance-segment drill-down exists.`}
      note={
        activity
          ? "Activity is a share of observed table-minutes, not visitor counts or occupancy duration. Read the coverage alongside it. Missing/unavailable periods remain gaps."
          : "One reading per table per minute. Light baseline/markers mean observed zero; gaps mean unavailable/missing scans. Hover for exact counts; click for raw scans. Across tables, advertiser counts can overlap."
      }
      titleRight={
        <select
          aria-label="Presence chart view"
          value={metric}
          onChange={(event) => {
            setMetric(event.target.value as Metric);
            setFocusRange(null);
            setInspectedTime(null);
          }}
          className="rounded border border-border-subtle bg-panel-2 px-2 py-1 text-xs text-text-primary"
        >
          <option value="activity">Activity (% observed minutes)</option>
          <option value="phoneCounts">Possible phones (minute counts)</option>
          <option value="advertiserCounts">All advertisers (minute counts)</option>
        </select>
      }
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-text-secondary">
        <span>
          {activity
            ? "% of successful table-minutes"
            : `${metric === "phoneCounts" ? "Possible phone advertisers" : "All advertiser observations"} · count`}
        </span>
        <div className="flex flex-wrap items-center gap-1">
          {activity && (
            <select
              aria-label="Activity time resolution"
              value={resolution}
              onChange={(event) => {
                setResolution(event.target.value as ActivityResolution | "auto");
                setInspectedTime(null);
              }}
              className="rounded border border-border-subtle bg-panel-2 px-1 py-1 text-text-primary"
            >
              <option value="auto">Auto ({automatic})</option>
              <option value="minute">Minute</option>
              <option value="hour">Hour</option>
              <option value="day">Day</option>
            </select>
          )}
          {focusRange ? (
            <button
              className="rounded border border-accent px-2 py-1 text-accent"
              onClick={() => {
                setFocusRange(null);
                setMetric("activity");
                setInspectedTime(null);
              }}
            >
              Back to activity
            </button>
          ) : (
            <div className="flex gap-1" aria-label="Presence chart time window">
              {[
                { minutes: 60, label: "1h" },
                { minutes: 360, label: "6h" },
                { minutes: 0, label: "All" },
              ].map((option) => (
                <button
                  key={option.minutes}
                  aria-pressed={windowMinutes === option.minutes}
                  onClick={() => {
                    setWindowMinutes(option.minutes);
                    setInspectedTime(null);
                  }}
                  className={`rounded border px-2 py-1 ${windowMinutes === option.minutes ? "border-accent text-accent" : "border-border"}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full"
        role="img"
        aria-label={`Stacked ${activity ? "presence activity percentages" : "minute-level advertiser counts"} by distance. Arrow keys inspect intervals; Enter opens raw scans.`}
        tabIndex={0}
        onMouseMove={(event) => setInspectedTime(pointerTime(event))}
        onClick={(event) => openInterval(pointerTime(event), event)}
        onAuxClick={(event) => {
          if (event.button === 1) {
            event.preventDefault();
            openInterval(pointerTime(event), event);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            event.preventDefault();
            setInspectedTime(
              Math.max(
                start,
                Math.min(last, hoverTime + (event.key === "ArrowLeft" ? -step : step)),
              ),
            );
          } else if (event.key === "Enter") openInterval(hoverTime);
        }}
      >
        <title>{`${activity ? "Activity percentages" : "Minute counts"} stacked by approximate distance. Click: Raw Events → all BLE scans for the inspected ${grain}.`}</title>
        {Array.from(
          { length: yMax < 4 ? yMax + 1 : 5 },
          (_, i) => (i * yMax) / (yMax < 4 ? yMax : 4),
        ).map((value) => (
          <g key={value}>
            <line
              x1={LEFT}
              x2={WIDTH - RIGHT}
              y1={y(value)}
              y2={y(value)}
              stroke="#2e4468"
              strokeWidth="1"
            />
            <text x={LEFT - 8} y={y(value) + 4} textAnchor="end" fill="#8ea2c9" fontSize="12">
              {value}
              {activity ? "%" : ""}
            </text>
          </g>
        ))}
        {labels.map((_, series) => (
          <g key={series}>
            {runs.map((run) => {
              const upper = run.map((point) => `${x(point.time)},${y(cumulative(point, series))}`);
              const lower = [...run]
                .reverse()
                .map(
                  (point) => `${x(point.time)},${y(series ? cumulative(point, series - 1) : 0)}`,
                );
              return (
                <g key={run[0]!.time}>
                  <path
                    d={`M${upper.join(" L")} L${lower.join(" L")} Z`}
                    fill={COLORS[series]}
                    fillOpacity="0.6"
                  />
                  <path
                    d={run
                      .slice(1)
                      .map((point, index) => {
                        const previous = run[index]!;
                        return point.values![series]! > 0 || previous.values![series]! > 0
                          ? `M${x(previous.time)},${y(cumulative(previous, series))} L${x(point.time)},${y(cumulative(point, series))}`
                          : "";
                      })
                      .join(" ")}
                    fill="none"
                    stroke={COLORS[series]}
                    strokeWidth="2"
                  />
                  {run.length === 1 && run[0]!.values![series]! > 0 && (
                    <circle
                      cx={x(run[0]!.time)}
                      cy={y(cumulative(run[0]!, series))}
                      r="3"
                      fill={COLORS[series]}
                    />
                  )}
                </g>
              );
            })}
          </g>
        ))}
        {runs.map((run) => (
          <g key={`observed-zero-${run[0]!.time}`}>
            <path
              data-observed-zero-line="true"
              d={run
                .slice(1)
                .map((point, index) => {
                  const previous = run[index]!;
                  return point.values!.every((value) => value === 0) &&
                    previous.values!.every((value) => value === 0)
                    ? `M${x(previous.time)},${y(0)} L${x(point.time)},${y(0)}`
                    : "";
                })
                .join(" ")}
              fill="none"
              stroke="#dce6ff"
              strokeWidth="2"
            />
            {run
              .filter(
                (point, index) =>
                  point.values!.every((value) => value === 0) &&
                  (index === 0 ||
                    index === run.length - 1 ||
                    run[index - 1]!.values!.some((value) => value > 0) ||
                    run[index + 1]!.values!.some((value) => value > 0)),
              )
              .map((point) => (
                <circle
                  key={point.time}
                  data-observed-zero={new Date(point.time).toISOString()}
                  cx={x(point.time)}
                  cy={y(0)}
                  r="3.5"
                  fill="#dce6ff"
                  stroke="#10213b"
                  strokeWidth="1"
                >
                  <title>{`${timeLabel(point.time)}: observed zero ${metric === "phoneCounts" ? "possible phones" : "advertisers"} (${point.observed} successful table observations). Click: Raw Events → scans for this minute.`}</title>
                </circle>
              ))}
          </g>
        ))}
        {ticks.map((time) => (
          <text
            key={time}
            x={x(time)}
            y={HEIGHT - 20}
            textAnchor="middle"
            fill="#8ea2c9"
            fontSize="12"
          >
            {showDay || grain === "day"
              ? `${formatDashboardDayLabel(new Date(time).toISOString().slice(0, 10))} `
              : ""}
            {grain !== "day" ? new Date(time).toISOString().slice(11, 16) : ""}
          </text>
        ))}
        <line
          x1={x(hoverTime)}
          x2={x(hoverTime)}
          y1={TOP}
          y2={TOP + PLOT_HEIGHT}
          stroke="#dce6ff"
          strokeDasharray="4 4"
          opacity="0.5"
        />
      </svg>
      <div
        className="min-h-[92px] rounded border border-border bg-panel-2 px-2 py-2 text-[11px]"
        aria-live="polite"
      >
        <div className="mb-1 flex flex-wrap justify-between gap-1 font-semibold">
          <span>
            {timeLabel(hoverTime)}
            {activity && grain !== "minute" ? ` · ${grain}` : ""}
          </span>
          {activity && hovered && step > PRESENCE_MINUTE && (
            <button
              className="text-accent underline"
              onClick={() => {
                setFocusRange({
                  from: Math.max(hoverTime, Date.parse(data.range.from)),
                  to: Math.min(hoverTime + step - 1, Date.parse(data.range.to)),
                });
                setMetric("phoneCounts");
                setWindowMinutes(0);
                setInspectedTime(null);
              }}
            >
              Inspect {grain} as minute counts
            </button>
          )}
        </div>
        {hovered?.values ? (
          <>
            {activity && (
              <div className="mb-1 font-semibold text-ok">
                Near-table activity (~0–2 m):{" "}
                {pct(hovered.counts![0]! + hovered.counts![1]!, hovered.observed)} (
                {hovered.counts![0]! + hovered.counts![1]!} / {hovered.observed} observed
                table-minutes)
              </div>
            )}
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {hovered.values.map((value, index) => (
                <span key={index} style={{ color: COLORS[index] }}>
                  {labels[index]}:{" "}
                  <strong>
                    {activity ? `${value.toFixed(1)}% (${hovered.counts![index]})` : value}
                  </strong>
                </span>
              ))}
              {!activity && (
                <span>
                  Total: <strong>{hovered.values.reduce((sum, value) => sum + value, 0)}</strong>
                </span>
              )}
            </div>
          </>
        ) : (
          <div className="text-warn">
            {hovered
              ? "Scans unavailable — activity not observed."
              : "No scans received for this interval."}
          </div>
        )}
        <div
          className={`mt-1 ${activity && (hovered?.observed ?? 0) < possible ? "text-warn" : "text-text-secondary"}`}
        >
          {activity
            ? `Coverage: ${hovered?.observed ?? 0} / ${possible} possible table-minute slots observed (${pct(hovered?.observed ?? 0, possible)}); ${hovered?.reported ?? 0} reported. Low coverage can skew the activity percentage.`
            : hovered
              ? `${hovered.observed} / ${data.summary.tables} tables observed successfully · ${hovered.reported} tables reported this minute`
              : "No table coverage"}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-text-secondary">
        {!activity && (
          <span className="flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#dce6ff]" />
            Observed zero
          </span>
        )}
        {labels.map((label, index) => (
          <span key={label} className="flex items-center gap-1">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: COLORS[index] }}
            />
            {label}
          </span>
        ))}
      </div>
    </ChartCard>
  );
}
